import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, exportUrl, formatApiError } from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileSpreadsheet, FileDown, Printer, ChevronRight } from "lucide-react";

const FAMILY_REPORTS = [
  { group: "group_housing", items: [
    { id: "rep_own_house", filters: { own_house: "yes" } },
    { id: "rep_without_own_house", filters: { own_house: "no" } },
    { id: "rep_pattas_issued", filters: { patta_issued: "yes" } },
    { id: "rep_requiring_patta", filters: { needs_patta: "yes" } },
  ]},
  { group: "group_agriculture", items: [
    { id: "rep_land_owners", filters: { owns_land: "yes" } },
    { id: "rep_landless", filters: { owns_land: "no" } },
  ]},
  { group: "group_crops", items: [
    { id: "rep_crop_farmers", filters: { owns_land: "yes" } },
  ]},
  { group: "group_borewell", items: [
    { id: "rep_gov_bw", filters: { borewell_type: "Government" } },
    { id: "rep_pvt_bw", filters: { borewell_type: "Private" } },
    { id: "rep_working_bw", filters: { borewell_status: "Working" } },
    { id: "rep_nonworking_bw", filters: { borewell_status: "Not Working" } },
    { id: "rep_dry_bw", filters: { borewell_status: "Dry" } },
    { id: "rep_repair_bw", filters: { borewell_status: "Repair Required" } },
    { id: "rep_requiring_bw", filters: { needs_borewell: "yes" } },
  ]},
  { group: "group_electricity", items: [
    { id: "rep_bw_with_elec", filters: { bw_electricity: "yes" } },
    { id: "rep_bw_without_elec", filters: { bw_electricity: "no" } },
    { id: "rep_bw_req_elec", filters: { bw_electricity: "no" } },
  ]},
];

const AGG_REPORTS = [
  { group: "group_demographics", items: [
    { id: "rep_gender", chartKey: "gender", nameKey: "name" },
    { id: "rep_age_groups", chartKey: "age_groups", nameKey: "name" },
    { id: "rep_marital", chartKey: "marital_status", nameKey: "name" },
  ]},
  { group: "group_education", items: [
    { id: "rep_education_dist", chartKey: "education", nameKey: "name" },
  ]},
  { group: "group_profession", items: [
    { id: "rep_profession_dist", chartKey: "profession", nameKey: "name" },
  ]},
  { group: "group_crops", items: [
    { id: "rep_seasonal", chartKey: "season_distribution", nameKey: "season", valueKey: "count" },
  ]},
  { group: "group_caste", items: [
    { id: "rep_caste_dist", chartKey: "caste", nameKey: "category", caste: true },
  ]},
];

export default function Reports() {
  const { t } = useLang();
  const { scope } = useAuth();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null); // {type:'families'|'agg', ...}
  const [rows, setRows] = useState(null);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(false);
  const resultsRef = useRef(null);

  const scrollToResults = () => {
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    api.get("/dashboard", { params: { scope } }).then(({ data }) => setDash(data)).catch(() => {});
  }, [scope]);

  const runFamilyReport = async (rep) => {
    setSelected({ type: "families", ...rep });
    setLoading(true);
    scrollToResults();
    try {
      const { data } = await api.get("/families", { params: { ...rep.filters, scope } });
      setRows(data.families);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const runAggReport = (rep) => {
    setSelected({ type: "agg", ...rep });
    setRows(null);
    scrollToResults();
  };

  const exportParams = selected?.type === "families" ? { ...selected.filters, scope } : { scope };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <h1 className="text-2xl md:text-3xl tracking-tight font-semibold text-graphite" data-testid="reports-title">{t("reports_title")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("reports_subtitle")}</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-5">
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-5 h-fit no-print" data-testid="reports-menu">
          {FAMILY_REPORTS.map((g) => (
            <div key={g.group}>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-2">{t(g.group)}</div>
              <div className="space-y-0.5">
                {g.items.map((rep) => (
                  <button
                    key={rep.id}
                    data-testid={`report-${rep.id}`}
                    onClick={() => runFamilyReport(rep)}
                    className={`w-full text-left flex items-center justify-between px-2.5 py-2 rounded-md text-sm transition-colors duration-150 ${
                      selected?.id === rep.id ? "bg-red-50 text-brand-red font-medium" : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {t(rep.id)}
                    <ChevronRight className="h-3.5 w-3.5 opacity-50" />
                  </button>
                ))}
              </div>
            </div>
          ))}
          {AGG_REPORTS.map((g) => (
            <div key={g.group + "-agg"}>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-2">{t(g.group)}</div>
              <div className="space-y-0.5">
                {g.items.map((rep) => (
                  <button
                    key={rep.id}
                    data-testid={`report-${rep.id}`}
                    onClick={() => runAggReport(rep)}
                    className={`w-full text-left flex items-center justify-between px-2.5 py-2 rounded-md text-sm transition-colors duration-150 ${
                      selected?.id === rep.id ? "bg-red-50 text-brand-red font-medium" : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {t(rep.id)}
                    <ChevronRight className="h-3.5 w-3.5 opacity-50" />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div ref={resultsRef} className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-5 min-h-[400px] scroll-mt-24" data-testid="report-results">
          {!selected ? (
            <div className="flex items-center justify-center h-64 text-sm text-slate-400">{t("select_report")}</div>
          ) : loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="h-10 w-10 rounded-full border-2 border-brand-red border-t-transparent animate-spin" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-heading font-medium text-graphite" data-testid="report-result-title">{t(selected.id)}</h2>
                  {selected.type === "families" && rows && (
                    <p className="text-xs text-slate-500 mt-0.5" data-testid="report-result-count">{rows.length} {t("families_count").toLowerCase()}</p>
                  )}
                </div>
                <div className="flex gap-2 no-print">
                  <Button data-testid="report-print-btn" variant="outline" size="sm" onClick={() => window.print()}>
                    <Printer className="h-4 w-4 mr-1.5" />{t("print")}
                  </Button>
                  <Button data-testid="report-export-excel-btn" variant="outline" size="sm" onClick={() => window.open(exportUrl("/export/excel", exportParams), "_blank")}>
                    <FileSpreadsheet className="h-4 w-4 mr-1.5 text-agri" />{t("export_excel")}
                  </Button>
                  <Button data-testid="report-export-pdf-btn" variant="outline" size="sm" onClick={() => window.open(exportUrl("/export/pdf", exportParams), "_blank")}>
                    <FileDown className="h-4 w-4 mr-1.5 text-brand-red" />{t("export_pdf")}
                  </Button>
                </div>
              </div>

              {selected.type === "families" && rows && (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs">{t("col_family_id")}</TableHead>
                      <TableHead className="text-xs">{t("col_head")}</TableHead>
                      <TableHead className="text-xs">{t("col_ward")}</TableHead>
                      <TableHead className="text-xs text-center">{t("col_members")}</TableHead>
                      <TableHead className="text-xs">{t("col_caste")}</TableHead>
                      <TableHead className="text-xs text-right">{t("col_land")}</TableHead>
                      <TableHead className="text-xs text-center">{t("col_borewells")}</TableHead>
                      <TableHead className="text-xs">{t("col_crops")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((f) => (
                      <TableRow key={f.family_id} className="report-row hover:bg-slate-50 cursor-pointer" data-testid={`report-row-${f.family_id}`}
                        onClick={() => navigate(`/families/${f.family_id}`)}>
                        <TableCell className="font-mono text-xs text-water">{f.family_id}</TableCell>
                        <TableCell className="text-sm">{f.head_name}</TableCell>
                        <TableCell className="text-xs text-slate-500">{f.ward}</TableCell>
                        <TableCell className="text-center text-sm">{f.member_count}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{f.caste}</Badge></TableCell>
                        <TableCell className="text-right text-sm">{f.total_land_acres || "—"}</TableCell>
                        <TableCell className="text-center text-sm">{f.borewell_count || "—"}</TableCell>
                        <TableCell className="text-xs text-slate-500">{f.crops.join(", ") || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {selected.type === "families" && rows && rows.length === 0 && (
                <div className="py-16 text-center text-sm text-slate-400">{t("no_families")}</div>
              )}

              {selected.type === "agg" && dash && (
                <>
                  {selected.caste && (
                    <p className="text-[11px] text-slate-400 mb-3">{t("caste_disclaimer")}</p>
                  )}
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs">{selected.caste ? t("category") : selected.id === "rep_education_dist" ? t("education_level") : selected.id === "rep_profession_dist" ? t("profession_label") : t("category")}</TableHead>
                        {selected.caste && <TableHead className="text-xs text-right">{t("families_count")}</TableHead>}
                        <TableHead className="text-xs text-right">{selected.caste ? t("people") : t("count")}</TableHead>
                        {selected.caste && <TableHead className="text-xs text-right">{t("percentage")}</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(dash.charts[selected.chartKey] || []).map((row, i) => (
                        <TableRow key={i} data-testid={`agg-row-${i}`}>
                          <TableCell className="text-sm font-medium text-graphite">{row[selected.nameKey]}</TableCell>
                          {selected.caste && <TableCell className="text-sm text-right">{row.families}</TableCell>}
                          <TableCell className="text-sm text-right">{selected.caste ? row.people : row[selected.valueKey || "value"]}</TableCell>
                          {selected.caste && <TableCell className="text-sm text-right">{row.percentage}%</TableCell>}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
